import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Table, TableProps, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import EditableDrawer from 'components/EditableDrawer';
import SingleSelect from 'components/SingleSelect';
import FilterGroupRow from 'components/FilterGroupRow';
import { TmsRouteWaypointType } from '../../route/graphql/queries/getTmsRoutes';
import {
    GET_STATION_LIST_BY_BUSINESS_TYPE,
    GetStationListByBusinessTypeResponse,
} from '../../route/graphql/queries/getStationListByBusinessType';
import { useTmsVehicleAndModel } from '../../hooks/useTmsVehicleAndModel';
import { useTmsDriversByRoute } from '../../hooks/useTmsDriversByRoute';
import { useTmsVehiclesByRoute } from '../../hooks/useTmsVehiclesByRoute';
import { UPDATE_TMS_SCHEDULE, UpdateTmsScheduleInput } from '../graphql/mutations/scheduleMutations';
import MultiSelect from 'components/MultiSelect';
import { getWeekdaysOptions, getTmsRouteBusinessTypeLabel } from '../types/TmsScheduleType';
import InputFilterItem from 'components/InputFilterItem';
import TimePickerFilter from 'components/TimePickerFilter';
import { TmsScheduleType } from '../graphql/queries/getTmsSchedules';
import { TmsBusinessTypeEnum, TmsCarrierSourceTypeEnum, TmsRouteTypeEnum, HandoverTypeEnum, TmsStatusEnum, WaypointTypeEnum, NonFixedOption, DepartureDayOffsetEnum } from 'enums/TmsEnum';
import {
    formatTimeWithDayOffsetAndTimezone,
    TimeDisplay,
    calculateWaypointTimes,
    calculateDestinationArrivalTime,
    initializeOriginTime
} from '../../../../utils/tmsWaypointTimeUtils';
import { showError } from '../../../../utils/exceptionUtils';

interface FilterOptions {
    routeType: string;
    routeId: string | number;
    carrierId: string | number;
    status: string;
    departureFrequency: number[];
    driverId: string | number;
    vehicleId: string | number;
    vehicleModel: string;
    originPlannedDepartureTime: string;
    departureDayOffset: number;
}

interface EditScheduleDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: () => void;
    initialValues?: TmsScheduleType;
    waypoints?: TmsRouteWaypointType[];
    routeInfo?: {
        plannedTotalHours?: number;
        plannedTotalMiles?: number;
        routeType?: number;
    };
    statusOptions: { label: string; value: number }[];
    routeName?: string;
    /** 路线业务类型（与 getTmsRouteWaypointsWithSortingCodeByRouteId.routeBusinessType 一致） */
    routeBusinessType?: number | null;
    isHandoverTab?: boolean;
}

const EditScheduleDrawer: React.FC<EditScheduleDrawerProps> = ({
    visible,
    onClose,
    onSubmit,
    initialValues,
    waypoints,
    routeInfo,
    statusOptions,
    routeName,
    routeBusinessType,
    isHandoverTab = false,
}) => {
    const { t } = useTranslation();

    // 是否为交接类型路线（用 ref 缓存，关闭时 routeInfo 变 null，避免 label 闪跳）
    // 优先用 initialValues.routeType 立即判断，避免 routeInfo 异步加载导致闪烁
    const isHandoverRouteRef = useRef(initialValues?.routeType === TmsRouteTypeEnum.Handover);
    if (routeInfo) {
        isHandoverRouteRef.current = routeInfo.routeType === TmsRouteTypeEnum.Handover;
    } else if (initialValues?.routeType !== undefined) {
        isHandoverRouteRef.current = initialValues.routeType === TmsRouteTypeEnum.Handover;
    }
    const isHandoverRoute = isHandoverRouteRef.current;
    // 交接方类型是否为干线承运商（经外部站点、有终点/途经点配置）；本地尾程商（含 TikTok、TemuGA 选本地）则无
    const isLinehaulCarrier = initialValues?.carrierSourceType === HandoverTypeEnum.LinehaulCarrier;
    const isExternalStationMode = routeBusinessType === TmsBusinessTypeEnum.TemuGA || routeBusinessType === TmsBusinessTypeEnum.TemuSCF;
    const { data: stationData } = useQuery<GetStationListByBusinessTypeResponse>(GET_STATION_LIST_BY_BUSINESS_TYPE, {
        skip: !visible || !isExternalStationMode,
        variables: { businessType: routeBusinessType },
        fetchPolicy: 'cache-first',
    });
    const stationList = useMemo(
        () => stationData?.getStationListByBusinessType ?? [],
        [stationData?.getStationListByBusinessType]
    );
    const [filters, setFilters] = useState<FilterOptions>({
        routeType: '',
        routeId: '',
        carrierId: '',
        status: TmsStatusEnum.Active.toString(),
        departureFrequency: [1, 2, 3, 4, 5],
        driverId: '',
        vehicleId: '',
        vehicleModel: '',
        originPlannedDepartureTime: '',
        departureDayOffset: DepartureDayOffsetEnum.CurrentDay,
    });
    const [departureTime, setDepartureTime] = useState<Dayjs | null>(null);
    // 标记 vehicle 是否是从司机查询自动设置的
    const [isVehicleAutoSet, setIsVehicleAutoSet] = useState(false);
    // 记录司机绑定的车辆信息（如果司机有绑定车辆）
    const [driverBoundVehicle, setDriverBoundVehicle] = useState<{ id: number; licensePlate?: string; vehicleNumber?: string } | null>(null);

    // 将数据库中的 null 值转换为表单中的"非固定"选项值
    // 业务逻辑：数据库中 driverId/vehicleId/vehicleModelId 为 null 表示"非固定"，需要回显为对应的特殊值
    const getDriverIdForForm = (driverId: number | null | undefined): string => {
        return driverId != null ? driverId.toString() : NonFixedOption.Driver;
    };

    const getVehicleIdForForm = (vehicleId: number | null | undefined): string => {
        return vehicleId != null ? vehicleId.toString() : NonFixedOption.Vehicle;
    };

    const getVehicleModelIdForForm = (vehicleModelId: number | null | undefined): string => {
        return vehicleModelId != null ? vehicleModelId.toString() : NonFixedOption.Model;
    };

    // 初始化表单数据
    useEffect(() => {
        if (initialValues && visible) {
            // 解析 departureFrequency JSON 数组
            let departureFrequency: number[] = [];
            try {
                departureFrequency = JSON.parse(initialValues.departureFrequency || '[]');
            } catch (e) {
                departureFrequency = [];
            }

            // 解析时间（可能包含秒，如 HH:mm:ss）
            const timeStr = initialValues.planDepartureTime;
            const time = dayjs(timeStr, ['HH:mm:ss', 'HH:mm']);
            
            setFilters({
                routeType: '',
                routeId: initialValues.routeId.toString(),
                carrierId: initialValues.carrierId.toString(),
                status: initialValues.status.toString(),
                departureFrequency: departureFrequency,
                driverId: getDriverIdForForm(initialValues.driverId),
                vehicleId: getVehicleIdForForm(initialValues.vehicleId),
                vehicleModel: getVehicleModelIdForForm(initialValues.vehicleTypeId),
                originPlannedDepartureTime: time.isValid() ? time.format('HH:mm') : timeStr,
                departureDayOffset: initialValues.departureDayOffset ?? DepartureDayOffsetEnum.CurrentDay,
            });
            setDepartureTime(time.isValid() ? time : null);
            setIsVehicleAutoSet(false);
        }
    }, [initialValues, visible]);

    // 使用Hook根据路线获取司机
    const { driverOptions: originalDriverOptions } = useTmsDriversByRoute(filters.routeId);

    // 在司机选项前添加"非固定司机"选项
    const driverOptions = useMemo(() => {
        return [
            { label: String(t('tms.schedule.drawer.nonFixedDriver')), value: NonFixedOption.Driver },
            ...originalDriverOptions
        ];
    }, [originalDriverOptions, t]);
    
    // 使用Hook根据routeId自动查询车辆数据
    const { vehiclesData, vehicleOptions: originalVehicleOptions, selectedRoute } = useTmsVehiclesByRoute(filters.routeId);

    // 在车辆选项前添加“非固定车牌”选项
    const vehicleOptions = useMemo(() => {
        return [
            { label: String(t('tms.schedule.drawer.nonFixedVehicle')), value: NonFixedOption.Vehicle },
            ...originalVehicleOptions
        ];
    }, [originalVehicleOptions, t]);

    // 使用Hook查询车辆和车型信息
    const { getVehicleAndModel } = useTmsVehicleAndModel({
        onVehicleAndModelFound: (vehicleId, vehicleModelName, _vehicleModelId, vehicle) => {
                    // 设置车辆ID
                    handleFilterChange('vehicleId', vehicleId.toString());
                    // 设置车型名称
                    handleFilterChange('vehicleModel', vehicleModelName);
                    // 标记为自动设置
                    setIsVehicleAutoSet(true);
                    // 记录司机绑定的车辆信息
                    setDriverBoundVehicle(vehicle);
        },
        onVehicleAndModelNotFound: () => {
            // 如果返回 null，说明该司机没有分配车辆，清空车辆和车型，允许手动选择 Vehicle
            handleFilterChange('vehicleId', '');
            handleFilterChange('vehicleModel', '');
            setIsVehicleAutoSet(false);
            // 清空司机绑定的车辆信息
            setDriverBoundVehicle(null);
        },
        onError: (error) => {
            // 出错时也允许手动选择
            handleFilterChange('vehicleId', '');
            handleFilterChange('vehicleModel', '');
            setIsVehicleAutoSet(false);
            // 清空司机绑定的车辆信息
            setDriverBoundVehicle(null);
        }
    });

    // 根据 vehicleId 获取对应的 vehicleModelName
    const selectedVehicleModelName = useMemo(() => {
        if (!filters.vehicleId || !vehiclesData?.getTmsVehiclesWithModel) return '';
        const vehicle = vehiclesData.getTmsVehiclesWithModel.find(v => v.id.toString() === filters.vehicleId.toString());
        return vehicle?.vehicleTypeName || '';
    }, [filters.vehicleId, vehiclesData]);

    // 根据 vehicleId 获取对应的 vehicleTypeId
    const selectedVehicleTypeId = useMemo(() => {
        if (!filters.vehicleId || !vehiclesData?.getTmsVehiclesWithModel) return null;
        const vehicle = vehiclesData.getTmsVehiclesWithModel.find(v => v.id.toString() === filters.vehicleId.toString());
        return vehicle?.vehicleTypeId || null;
    }, [filters.vehicleId, vehiclesData]);

    // 当手动选择 vehicle 或初始化时，自动填充 vehicleModel（如果不是非固定车牌）
    useEffect(() => {
        if (filters.vehicleId && filters.vehicleId !== NonFixedOption.Vehicle && selectedVehicleTypeId && filters.vehicleModel !== selectedVehicleTypeId.toString()) {
            setFilters(prev => ({ ...prev, vehicleModel: selectedVehicleTypeId.toString() }));
        } else if (filters.vehicleId === NonFixedOption.Vehicle && filters.vehicleModel !== NonFixedOption.Model) {
            // 如果选择了非固定车牌，自动设置为非固定车型
            setFilters(prev => ({ ...prev, vehicleModel: NonFixedOption.Model }));
        } else if (!filters.vehicleId && filters.vehicleModel) {
            // 车辆被清空时，同步清空车型
            setFilters(prev => ({ ...prev, vehicleModel: '' }));
        }
    }, [filters.vehicleId, selectedVehicleTypeId, filters.vehicleModel]);

    // 计算车型显示值（用于只读显示）
    const vehicleModelDisplayValue = useMemo(() => {
        if (filters.vehicleModel === NonFixedOption.Model) {
            return String(t('tms.schedule.drawer.nonFixedVehicleModel'));
        }
        if (!filters.vehicleModel) {
            return '';
        }
        // 直接使用 selectedVehicleModelName，它已经根据 vehicleId 自动计算好了
        return selectedVehicleModelName || '';
    }, [filters.vehicleModel, selectedVehicleModelName, t]);

    // departureDayOffset 选项（编辑时仅用于展示）
    const departureDayOffsetOptions = useMemo(() => [
        { label: String(t('tms.schedule.drawer.previousDay')), value: DepartureDayOffsetEnum.PreviousDay.toString() },
        { label: String(t('tms.schedule.drawer.currentDay')), value: DepartureDayOffsetEnum.CurrentDay.toString() },
    ], [t]);

    const routeBusinessTypeDisplay = useMemo(
        () =>
            getTmsRouteBusinessTypeLabel(
                t,
                routeBusinessType ?? selectedRoute?.businessType ?? null
            ),
        [t, routeBusinessType, selectedRoute?.businessType]
    );

    // 交接方类型展示（仅交接路线使用，carrierSourceType 内部/三方已在交接方需求中废弃）
    // 取班次自身快照（initialValues），与交接方名称同源，避免「名字是班次的、类型是路线的」错配
    const handoverCarrierTypeDisplay = useMemo(() => {
        if (!isHandoverRoute) return '';
        const st = initialValues?.carrierSourceType;
        if (st === HandoverTypeEnum.LocalLastMileCarrier) {
            return String(t('tms.route.handoverTypes.localLastMileCarrier'));
        }
        if (st === HandoverTypeEnum.LinehaulCarrier) {
            return String(t('tms.route.handoverTypes.linehaulCarrier'));
        }
        return '-';
    }, [initialValues?.carrierSourceType, isHandoverRoute, t]);

    // 更新班次的 mutation
    const [updateSchedule, { loading: updating }] = useMutation(UPDATE_TMS_SCHEDULE);


    const handleFilterChange = (name: keyof FilterOptions, value: string | number | undefined | string[] | number[]) => {
        if (name === 'departureFrequency') {
            const numArray = (value as string[] || []).map(v => Number(v));
            setFilters(prev => ({ ...prev, [name]: numArray }));
        } else {
            setFilters(prev => ({ ...prev, [name]: value || '' }));
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleSubmit = async () => {
        if (!initialValues) {
            message.error(t('tms.schedule.messages.scheduleDataNotFound'));
            return;
        }

        // 验证必填字段
        if (!filters.routeId) {
            message.error(t('tms.schedule.messages.pleaseSelectRoute'));
            return;
        }
        if (!filters.originPlannedDepartureTime) {
            message.error(t(isHandoverRoute ? 'tms.task.messages.pleaseSelectHandoverTime' : 'tms.schedule.messages.pleaseSelectDepartureTime'));
            return;
        }
        if (!filters.status) {
            message.error(t('tms.schedule.messages.pleaseSelectStatus'));
            return;
        }
        if (filters.departureFrequency.length === 0) {
            message.error(t('tms.schedule.messages.pleaseSelectDepartureFrequency'));
            return;
        }
        if (!isHandoverRoute && !filters.driverId) {
            message.error(t('tms.schedule.messages.pleaseSelectDriver'));
            return;
        }
        // 如果选择了非固定司机，则不需要选择车辆
        if (!isHandoverRoute && filters.driverId !== NonFixedOption.Driver && !filters.vehicleId) {
            message.error(t('tms.schedule.messages.pleaseSelectVehicle'));
            return;
        }        

        try {
            // 使用 hook 返回的 selectedRoute 获取 startWarehouseId 作为 warehouseId
            const warehouseId = selectedRoute?.startWarehouseId 
                ? Number(selectedRoute.startWarehouseId) 
                : (initialValues?.warehouseId ? Number(initialValues.warehouseId) : undefined);
            
            if (!warehouseId) {
                message.error(t('tms.schedule.messages.routeStartWarehouseIdMissing'));
                return;
            }

            // 构建更新输入对象（只保存表单字段，不包含途经点数据、出发时间和路线）
            const input: UpdateTmsScheduleInput = {
                departureFrequency: JSON.stringify(filters.departureFrequency.sort()), // 转换为 JSON 字符串
                driverId: filters.driverId === NonFixedOption.Driver ? null : (filters.driverId ? Number(filters.driverId) : undefined),
                vehicleId: filters.vehicleId === NonFixedOption.Vehicle
                    ? null
                    : (filters.vehicleId ? Number(filters.vehicleId) : null),
                vehicleTypeId: filters.vehicleModel === NonFixedOption.Model
                    ? null
                    : (filters.vehicleModel ? Number(filters.vehicleModel) : null),
                warehouseId: warehouseId, // 使用 route 的 startWarehouseId
                status: Number(filters.status),
            };

            await updateSchedule({
                variables: {
                    id: initialValues.id,
                    input
                }
            });

            message.success(t('tms.schedule.messages.scheduleUpdatedSuccessfully'));
            // 先调用父组件的回调刷新列表，等待完成后再关闭抽屉
            await onSubmit(); // 调用父组件的回调，可能用于刷新列表
            handleClose(); // 关闭抽屉
        } catch (error: any) {
            console.error('Failed to update schedule:', error);
            const hasMessageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
            hasMessageKey ? showError(error) : message.error(error?.message || t('tms.schedule.messages.failedToUpdateSchedule'));
        }
    };

    const drawerTitle = initialValues?.scheduleName || String(t('tms.schedule.drawer.editTitle'));

    interface WaypointDataType {
        key: string;
        waypointType: string; // 'Origin', 'Waypoint 1', 'Waypoint 2', 'Waypoint 3', 'Destination'
        waypointId: number;
        waypointName: string;
        plannedArrival: TimeDisplay | string; // 如果是 '-' 则为字符串，否则为 TimeDisplay
        plannedDeparture: TimeDisplay | string; // 如果是 '-' 则为字符串，否则为 TimeDisplay
    }

    // 渲染时间，将时区信息换行显示
    const renderTimeWithTimezone = (timeDisplay: TimeDisplay | string) => {
      if (!timeDisplay || timeDisplay === '-') return timeDisplay;
      
      if (typeof timeDisplay === 'string') {
        return timeDisplay;
      }
      
      const { time, timezone } = timeDisplay;
      if (!timezone) {
        return time;
      }
      
      return (
        <div>
          <div>{time}</div>
          <div>({timezone.toUpperCase()})</div>
        </div>
      );
    };

    const columns: TableProps<WaypointDataType>['columns'] = useMemo(() => [
        {
          title: '',
          dataIndex: 'waypointType',
          key: 'waypointType',
          width: 120,
          align: 'left',
        },
        {
          title: t('tms.schedule.drawer.waypoint'),
          dataIndex: 'waypointName',
          key: 'waypointName',
          align: 'left',
        },
        {
          title: t('tms.schedule.drawer.plannedArrival'),
          dataIndex: 'plannedArrival',
          key: 'plannedArrival',
          align: 'left',
          render: (value: TimeDisplay | string) => renderTimeWithTimezone(value),
        },
        {
          title: t('tms.schedule.drawer.plannedDeparture'),
          dataIndex: 'plannedDeparture',
          key: 'plannedDeparture',
          align: 'left',
          render: (value: TimeDisplay | string) => renderTimeWithTimezone(value),
        },
      ], [t]);

    // 将途经点数据转换为表格数据，并计算计划到达和出发时间
    const waypointTableData = useMemo(() => {
        if (!waypoints || waypoints.length === 0 || !departureTime) {
            return [];
        }

        const isExternalStationRoute = routeBusinessType === TmsBusinessTypeEnum.TemuGA || routeBusinessType === TmsBusinessTypeEnum.TemuSCF;
        const resolveWaypointDisplayName = (waypoint: TmsRouteWaypointType): string => {
            if (
                isExternalStationRoute &&
                (waypoint.waypointType === WaypointTypeEnum.Waypoint ||
                    waypoint.waypointType === WaypointTypeEnum.Destination)
            ) {
                const extId = waypoint.extWaypointId;
                if (extId != null) {
                    const cfg = stationList.find(c => c.id != null && c.id === extId);
                    const name = (cfg?.sortingCode || '').trim();
                    if (name) {
                        return name;
                    }
                }
                return (waypoint.waypointSortingCode || '').trim() || '-';
            }
            return waypoint.waypointSortingCode || '-';
        };

        const result: WaypointDataType[] = [];
        // 按 waypointOrder 排序
        const sortedWaypoints = [...waypoints].sort((a, b) => a.waypointOrder - b.waypointOrder);

        // 统计途经点数量（用于编号）
        let waypointIndex = 0;
        
        // 获取起点时区offset
        const firstWaypoint = sortedWaypoints[0];
        const baseOffset = firstWaypoint?.waypointTimezoneOffset ?? 0;

        // 所有计算都在起点时区进行
        // 初始化起点时间
        let currentTime = initializeOriginTime(
            departureTime.format('HH:mm'),
            baseOffset
        );

        // 基准日期（起点的日期，本地时间）
        const baseDate = currentTime.startOf('day');

        sortedWaypoints.forEach((waypoint) => {
            let waypointTypeLabel: string;
            let plannedArrival: TimeDisplay | string = '-';
            let plannedDeparture: TimeDisplay | string = '-';
            const wpOffset = waypoint.waypointTimezoneOffset ?? 0;
            const wpTimezone = waypoint.waypointTimezone;

            // 根据 waypointType 设置标签：1=起点，2=途经点，3=终点
            if (waypoint.waypointType === WaypointTypeEnum.Origin) {
                waypointTypeLabel = String(t('tms.schedule.drawer.origin'));
                // 起点：到达时间 = 发车时间 - 2小时（固定规则）
                const originArrivalTime = currentTime.clone().subtract(2, 'hour');
                plannedArrival = formatTimeWithDayOffsetAndTimezone(originArrivalTime, baseOffset, baseDate, wpOffset, wpTimezone);
                plannedDeparture = formatTimeWithDayOffsetAndTimezone(currentTime, baseOffset, baseDate, wpOffset, wpTimezone);
            } else if (waypoint.waypointType === WaypointTypeEnum.Waypoint) {
                waypointIndex++;
                waypointTypeLabel = String(t('tms.schedule.drawer.waypointX', { index: waypointIndex }));

                // 途经点：所有计算都在起点时区进行
                const { arrivalTime, departureTime: waypointDepartureTime } = calculateWaypointTimes(
                    currentTime,
                    waypoint.travelTime || 0,
                    waypoint.dwellTime || 0
                );

                // 将到达时间显示为当前途经点时区的本地时间
                plannedArrival = formatTimeWithDayOffsetAndTimezone(arrivalTime, baseOffset, baseDate, wpOffset, wpTimezone);

                // 将出发时间显示为当前途经点时区的本地时间
                plannedDeparture = formatTimeWithDayOffsetAndTimezone(waypointDepartureTime, baseOffset, baseDate, wpOffset, wpTimezone);

                // 更新当前时间点为这个途经点的出发时间（起点时区），用于下一个点的计算
                currentTime = waypointDepartureTime.clone();
            } else if (waypoint.waypointType === WaypointTypeEnum.Destination) {
                waypointTypeLabel = String(t('tms.schedule.drawer.destination'));

                // 终点：所有计算都在起点时区进行
                const arrivalTime = calculateDestinationArrivalTime(
                    currentTime,
                    waypoint.travelTime || 0
                );

                // 将到达时间显示为终点时区的本地时间
                plannedArrival = formatTimeWithDayOffsetAndTimezone(arrivalTime, baseOffset, baseDate, wpOffset, wpTimezone);

                // 终点没有出发时间
                plannedDeparture = '-';
            } else {
                waypointTypeLabel = String(t('tms.schedule.drawer.unknown', { type: waypoint.waypointType }));
            }

            result.push({
                key: `waypoint-${waypoint.id}`,
                waypointType: waypointTypeLabel,
                waypointId: waypoint.waypointId,
                waypointName: resolveWaypointDisplayName(waypoint),
                plannedArrival: plannedArrival,
                plannedDeparture: plannedDeparture,
            });
        });

        return result;
    }, [waypoints, filters.originPlannedDepartureTime, departureTime, t, routeBusinessType, stationList]);

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={handleClose}
            onSubmit={handleSubmit}
            confirmDisabled={updating}
            confirmLoading={updating}
            width={1200}
        >
            {/* 基本信息 */}
            <div className="bg-white mb-4">
                <div className="flex gap-4">
                    <div className="flex-1">
                        <div className="grid grid-cols-3 gap-4 mb-4">
                            <InputFilterItem
                                label={String(t('tms.schedule.drawer.route'))}
                                value={routeName || ''}
                                onChange={() => {}}
                                spanClassName="w-[7em]"
                                disabled={true}
                                required={true}
                                forceHorizontal={true}
                            />
                            <FilterGroupRow
                                label={String(t(isHandoverRoute ? 'tms.schedule.drawer.handoverTime' : 'tms.schedule.drawer.departureTime'))}
                                spanClassName="w-[7em]"
                                required
                                forceHorizontal
                                childWidths={['55%', '45%']}
                            >
                                <TimePickerFilter
                                    value={departureTime}
                                    onChange={(time) => {
                                        setDepartureTime(time);
                                        handleFilterChange('originPlannedDepartureTime', time ? time.format('HH:mm') : '');
                                    }}
                                    format="HH:mm"
                                    placeholder={String(t('tms.schedule.drawer.selectDepartureTime'))}
                                    minuteStep={5}
                                    showNow={false}
                                    disabled={true}
                                />
                                <SingleSelect
                                    options={departureDayOffsetOptions}
                                    value={filters.departureDayOffset.toString()}
                                    onChange={() => {}}
                                    disabled={true}
                                />
                            </FilterGroupRow>
                            <SingleSelect
                                label={String(t('tms.schedule.drawer.status'))}
                                options={statusOptions}
                                value={filters.status}
                                onChange={value => handleFilterChange('status', value)}
                                spanClassName="w-[7em]"
                                required={true}
                                forceHorizontal={true}
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                            <InputFilterItem
                                label={String(t('tms.schedule.drawer.routeBusinessType'))}
                                placeholder={String(t('tms.schedule.drawer.autoFilledFromRoute'))}
                                value={routeBusinessTypeDisplay}
                                onChange={() => {}}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal={true}
                            />
                            {isHandoverRoute && (
                            <InputFilterItem
                                label={String(t('tms.schedule.drawer.handoverCarrierType'))}
                                placeholder={String(t('tms.schedule.drawer.autoFilledFromRoute'))}
                                value={handoverCarrierTypeDisplay}
                                onChange={() => {}}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal={true}
                            />
                            )}
                            <InputFilterItem
                                label={String(t(isHandoverRoute ? 'tms.schedule.drawer.handoverCarrier' : 'tms.schedule.drawer.carrier'))}
                                placeholder={String(t('tms.schedule.drawer.autoFilledFromRoute'))}
                                value={initialValues?.carrierName ? String(initialValues.carrierName) : ''}
                                onChange={() => {}}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal={true}
                            />
                        </div>
                        <div className="mb-4">
                            <MultiSelect
                                label={String(t('tms.schedule.drawer.departureFrequency'))}
                                options={getWeekdaysOptions(t)}
                                value={filters.departureFrequency.map(v => v.toString())}
                                onChange={value => handleFilterChange('departureFrequency', value)}
                                spanClassName="w-[7em]"
                                required={true}
                                forceHorizontal={true}
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                            <SingleSelect
                                label={String(t('tms.schedule.drawer.driver'))}
                                placeholder={String(t('tms.schedule.drawer.selectDriver'))}
                                options={driverOptions}
                                value={filters.driverId ? filters.driverId.toString() : ''}
                                onChange={(value) => {
                                    handleFilterChange('driverId', value);
                                    // 每次切换司机时，先清空车辆与车型的自动设置标记和绑定信息
                                    setIsVehicleAutoSet(false);
                                    setDriverBoundVehicle(null);

                                    if (value === NonFixedOption.Driver) {
                                        // 选择非固定司机：默认设置为非固定车牌 + 非固定车型
                                        handleFilterChange('vehicleId', NonFixedOption.Vehicle);
                                        handleFilterChange('vehicleModel', NonFixedOption.Model);
                                    } else if (value) {
                                        // 选择具体司机：清空当前车辆和车型，等待自动查询结果
                                        handleFilterChange('vehicleId', '');
                                        handleFilterChange('vehicleModel', '');
                                        getVehicleAndModel({
                                            variables: { driverId: parseInt(value.toString()) }
                                        });
                                    } else {
                                        // 未选择司机：清空车辆和车型
                                        handleFilterChange('vehicleId', '');
                                        handleFilterChange('vehicleModel', '');
                                    }
                                }}
                                spanClassName="w-[7em]"
                                required={!isHandoverRoute}
                                forceHorizontal={true}
                            />
                            <SingleSelect
                                label={String(t('tms.schedule.drawer.vehicle'))}
                                placeholder={String(t('tms.schedule.drawer.selectVehicle'))}
                                options={vehicleOptions}
                                value={filters.vehicleId ? filters.vehicleId.toString() : ''}
                                onChange={(value) => {
                                    handleFilterChange('vehicleId', value);
                                    // 如果选择的是"非固定车牌"，自动设置为非固定车型
                                    if (value === NonFixedOption.Vehicle) {
                                        handleFilterChange('vehicleModel', NonFixedOption.Model);
                                    } else if (!value) {
                                        // 车辆被清空时，同步清空车型
                                        handleFilterChange('vehicleModel', '');
                                    }
                                    // 手动选择 vehicle 时，标记为非自动设置
                                    setIsVehicleAutoSet(false);
                                }}
                                spanClassName="w-[7em]"
                                required={!isHandoverRoute}
                                forceHorizontal={true}
                            />
                            <InputFilterItem
                                label={String(t('tms.schedule.drawer.vehicleModel'))}
                                placeholder={String(t('tms.schedule.drawer.autoFilledFromVehicle'))}
                                value={vehicleModelDisplayValue}
                                spanClassName="w-[7em]"
                                disabled={true}
                                forceHorizontal={true}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* 配置区（总用时/里程 + 途经点计划到达/发车）：交接路线仅干线承运商展示，本地尾程商（含 TemuGA DHLe）无终点不展示 */}
            {(!isHandoverRoute || isLinehaulCarrier) && (
                <>
                    {/* 总用时和总里程 */}
                    <div className="bg-white mb-4">
                        {routeInfo && ((routeInfo.plannedTotalHours !== undefined && routeInfo.plannedTotalHours !== null) || (routeInfo.plannedTotalMiles !== undefined && routeInfo.plannedTotalMiles !== null)) && (
                            <div className="mb-4 flex gap-6 text-sm">
                                {routeInfo.plannedTotalHours !== undefined && routeInfo.plannedTotalHours !== null && (
                                    <div className="text-gray-600">
                                       <span>{t('tms.schedule.drawer.totalHours')} </span><span className="font-bold">{routeInfo.plannedTotalHours} {t('tms.schedule.drawer.hours')}</span>
                                    </div>
                                )}
                                {routeInfo.plannedTotalMiles !== undefined && routeInfo.plannedTotalMiles !== null && (
                                    <div className="text-gray-600">
                                        <span>{t('tms.schedule.drawer.totalDistances')} </span><span className="font-bold">{routeInfo.plannedTotalMiles} {t('tms.schedule.drawer.miles')}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* 途经点表单 */}
                    <div className="bg-white mb-4 ">
                        <div className="flex gap-4">
                            <div className="flex-1">

                                <div className="mb-4">
                                    <Table<WaypointDataType>
                                        columns={columns}
                                        dataSource={waypointTableData}
                                        bordered
                                        pagination={false}
                                        size="middle"
                                        className="mb-4"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </EditableDrawer>
    );
};

export default EditScheduleDrawer;


